import { useEffect, useRef, useState } from "react";
import { JobsStatus } from "../../constants/Jobs";
import { planGateApplies, type JobsQueryParams } from "../../utils/jobsQuery";
import {
    getPlanJobGlobalIdsForVersion,
    JOBS_PAGE_SIZE_UI,
    queryJobsCount,
    queryJobsPage,
} from "../../utils/jobsUtils";
import type Graphic from "@arcgis/core/Graphic";

/** Delays a value so each keystroke does not become a server query. */
export const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
    const [debounced, setDebounced] = useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs]);

    return debounced;
};

export interface UseJobsListArgs {
    status: JobsStatus;
    /** Already debounced by the caller. */
    searchText: string;
    /** Username for "Mine jobber"; null for "Alle". */
    owner: string | null;
    versionGuid: string | null | undefined;
    /** Bump to force a refetch without changing the query. */
    refreshNonce: number;
    /** When true, no query is issued and the result is presented as an empty, non-loading list. */
    skip?: boolean;
}

export interface UseJobsListResult {
    jobs: Graphic[];
    total: number;
    page: number;
    totalPages: number;
    loading: boolean;
    setPage: (page: number) => void;
}

export const useJobsList = ({
    status,
    searchText,
    owner,
    versionGuid,
    refreshNonce,
    skip = false,
}: UseJobsListArgs): UseJobsListResult => {
    const [jobs, setJobs] = useState<Graphic[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);

    // Bumped on every fetch; stale resolutions check this before writing state so a slow
    // response cannot overwrite a newer list.
    const fetchSeqRef = useRef<number>(0);
    // The count is only re-queried when the query itself changes, not on page steps.
    const countKeyRef = useRef<string | null>(null);

    const queryKey = `${status}|${searchText}|${owner ?? ""}|${versionGuid ?? ""}|${refreshNonce}`;

    useEffect(() => {
        // Bumped even when skipped, so any earlier in-flight fetch is invalidated and cannot
        // write stale results after the skip takes effect.
        const seq = ++fetchSeqRef.current;

        if (skip) {
            setLoading(false);
            setJobs([]);
            setTotal(0);
            return;
        }

        setLoading(true);

        const run = async () => {
            try {
                // Skip the Steps prefilter entirely when the plan gate cannot affect the result.
                const planGlobalIds = planGateApplies(status)
                    ? await getPlanJobGlobalIdsForVersion(versionGuid)
                    : [];
                if (seq !== fetchSeqRef.current) return;

                const params: JobsQueryParams = { status, searchText, owner, planGlobalIds };
                const needsCount = countKeyRef.current !== queryKey;

                const [count, features] = await Promise.all([
                    needsCount ? queryJobsCount(params) : Promise.resolve(null),
                    queryJobsPage(params, page, JOBS_PAGE_SIZE_UI),
                ]);
                if (seq !== fetchSeqRef.current) return;

                if (needsCount && count !== null) {
                    countKeyRef.current = queryKey;
                    setTotal(count);

                    // The count for this same query key may have shrunk (e.g. a job closed
                    // elsewhere, or a stranded page from a previous count-fetch failure). If the
                    // current page no longer exists, clamp it and let the effect rerun for the
                    // clamped page instead of rendering the stale page's jobs against the new total.
                    const newTotalPages = Math.max(1, Math.ceil(count / JOBS_PAGE_SIZE_UI) || 1);
                    if (page > newTotalPages) {
                        setPage(newTotalPages);
                        return;
                    }
                }
                setJobs(features);
            } catch (error) {
                console.error("Kunne ikke hente jobber", error);
                if (seq === fetchSeqRef.current) {
                    setJobs([]);
                    setTotal(0);
                    // Force the count to be re-queried on the next attempt for this query key,
                    // instead of being permanently stuck at 0 by a transient failure.
                    countKeyRef.current = null;
                }
            } finally {
                if (seq === fetchSeqRef.current) setLoading(false);
            }
        };

        void run();
    }, [queryKey, page, status, searchText, owner, versionGuid, skip]);

    return {
        jobs,
        total,
        page,
        totalPages: Math.max(1, Math.ceil(total / JOBS_PAGE_SIZE_UI) || 1),
        loading,
        setPage,
    };
};
